import React, { ReactElement, useEffect, useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DirectionsBikeOutlinedIcon from '@mui/icons-material/DirectionsBikeOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import SmartButtonOutlinedIcon from '@mui/icons-material/SmartButtonOutlined';
import { ChatActionKind, ChatMessageActionInput } from '../../hooks/chat/api';
import { RiderRentals, useRiderRentals } from '../../hooks/chat/useRiderRentals';
import { MessageAction } from '../../hooks/useChat';
import {
  ACTION_KINDS,
  ActionDraft,
  ActionErrors,
  MAX_ACTIONS,
  MAX_LABEL_LENGTH,
  actionKindInfo,
  hasErrors,
  newActionDraft,
  pickRentalId,
  rentalChoices,
  toActionInput,
  validateAction,
} from './actionDrafts';

const NO_ACTIVE_RENTAL = 'This rider has no active rental';

const KIND_ICON: Record<string, ReactElement> = {
  reply: <ChatBubbleOutlineIcon fontSize="small" />,
  open_rental: <DirectionsBikeOutlinedIcon fontSize="small" />,
  retry_unlock: <LockOpenOutlinedIcon fontSize="small" />,
  end_rental: <StopCircleOutlinedIcon fontSize="small" />,
  add_payment_method: <CreditCardOutlinedIcon fontSize="small" />,
  open_url: <LinkOutlinedIcon fontSize="small" />,
};

/** What the chip's tooltip says the button does. */
function actionDetail(kind: string, value: string | null, rentalId: string | null) {
  const title = actionKindInfo(kind)?.title ?? kind;
  if (kind === 'open_url' && value) return `${title}: ${value}`;
  if (kind === 'reply' && value) return `${title}: sends "${value}"`;
  if (rentalId) return `${title} (rental ${rentalId.slice(0, 8)})`;
  return title;
}

/** A button of a sent reply, as the thread shows it. */
export function MessageActionChips({
  actions,
  align,
}: {
  actions: MessageAction[];
  align: 'flex-start' | 'flex-end';
}) {
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={0.5}
      justifyContent={align}
      sx={{ mt: 0.5 }}
      data-testid="message-actions"
    >
      {actions.map((a, i) => (
        <Tooltip
          key={`${a.kind}-${i}`}
          title={actionDetail(a.kind, a.value, a.rental_id)}
        >
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            icon={KIND_ICON[a.kind]}
            label={a.label}
            data-testid="message-action-chip"
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

/** The buttons attached to the reply being written, removable until it is sent. */
export function DraftActionChips({
  actions,
  onRemove,
  disabled,
}: {
  actions: ChatMessageActionInput[];
  onRemove: (index: number) => void;
  disabled?: boolean;
}) {
  if (actions.length === 0) return null;
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={0.5}
      alignItems="center"
      sx={{ px: 2, pt: 1 }}
      data-testid="draft-actions"
    >
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        Buttons:
      </Typography>
      {actions.map((a, i) => (
        <Tooltip
          key={`${a.kind}-${i}`}
          title={actionDetail(a.kind, a.value ?? null, a.rentalId ?? null)}
        >
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            icon={KIND_ICON[a.kind]}
            label={a.label}
            onDelete={disabled ? undefined : () => onRemove(i)}
            data-testid="draft-action-chip"
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

/**
 * The small form for one button: label, and what its kind needs. A rental
 * kind picks one of the rider's rentals; typing its id is the way out when
 * they cannot be read, the chat has no rider, or none is listed (an admin
 * who cannot read the rider's system sees none).
 */
export function ActionFormDialog({
  draft: initial,
  rentals,
  onCancel,
  onAdd,
}: {
  /** Null keeps the dialog closed. */
  draft: ActionDraft | null;
  rentals: RiderRentals;
  onCancel: () => void;
  onAdd: (action: ChatMessageActionInput) => void;
}) {
  const [draft, setDraft] = useState<ActionDraft | null>(initial);
  const [touched, setTouched] = useState(false);
  /** The rentals could not be read and the agent chose to type the id. */
  const [typingRentalId, setTypingRentalId] = useState(false);
  useEffect(() => {
    setDraft(initial);
    setTouched(false);
    setTypingRentalId(false);
  }, [initial]);

  const info = draft ? actionKindInfo(draft.kind) : undefined;
  const picking = Boolean(info?.needsRental) && !typingRentalId;
  const listed = rentals.status === 'ready';
  const choices =
    draft && picking
      ? rentalChoices(draft.kind, rentals.active, rentals.endedAnchor)
      : [];
  // What would be sent: a picked rental is always one the picker lists.
  const toSend =
    draft && picking
      ? { ...draft, rentalId: pickRentalId(choices, draft.rentalId) }
      : draft;
  const blocked = picking && (!listed || rentals.refreshing || choices.length === 0);
  const errors: ActionErrors = toSend && touched ? validateAction(toSend) : {};
  const set = (field: keyof ActionDraft) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => (d ? { ...d, [field]: e.target.value } : d));

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!toSend || blocked) return;
    setTouched(true);
    if (hasErrors(validateAction(toSend))) return;
    onAdd(toActionInput(toSend));
  };

  let rentalField: ReactElement | null = null;
  if (info?.needsRental && !picking) {
    rentalField = (
      <TextField
        autoFocus
        label="Rental id"
        value={draft?.rentalId ?? ''}
        onChange={set('rentalId')}
        error={Boolean(errors.rentalId)}
        helperText={
          errors.rentalId ?? 'The rental this conversation is about, by default'
        }
        inputProps={{ 'aria-label': 'Rental id' }}
        size="small"
      />
    );
  } else if (
    info?.needsRental &&
    (rentals.status === 'error' || rentals.status === 'unlinked')
  ) {
    rentalField = (
      <Stack alignItems="flex-start" spacing={0.5}>
        <Typography
          variant="body2"
          color={rentals.status === 'error' ? 'error' : 'text.secondary'}
        >
          {rentals.status === 'error'
            ? "Could not load the rider's rentals."
            : 'This conversation is not linked to a rider account.'}
        </Typography>
        <Button size="small" onClick={() => setTypingRentalId(true)}>
          Enter a rental id instead
        </Button>
      </Stack>
    );
  } else if (info?.needsRental && listed && choices.length === 0) {
    rentalField = (
      <Stack alignItems="flex-start" spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          {NO_ACTIVE_RENTAL}
        </Typography>
        <Button size="small" onClick={() => setTypingRentalId(true)}>
          Enter a rental id instead
        </Button>
      </Stack>
    );
  } else if (info?.needsRental) {
    rentalField = (
      <TextField
        select
        label="Rental"
        value={toSend?.rentalId ?? ''}
        onChange={set('rentalId')}
        disabled={!listed}
        error={Boolean(errors.rentalId)}
        helperText={
          errors.rentalId ??
          (!listed
            ? "Loading the rider's rentals…"
            : rentals.refreshing
            ? "Checking the rider's rentals…"
            : rentals.active.length === 0
            ? NO_ACTIVE_RENTAL
            : undefined)
        }
        size="small"
      >
        {choices.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  return (
    <Dialog open={Boolean(initial)} onClose={onCancel} fullWidth maxWidth="xs">
      <form onSubmit={submit} noValidate>
        <DialogTitle>{info ? `Add button: ${info.title}` : 'Add button'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {info && (
              <Typography variant="body2" color="text.secondary">
                {info.description}.
              </Typography>
            )}
            <TextField
              autoFocus
              label="Button label"
              value={draft?.label ?? ''}
              onChange={set('label')}
              error={Boolean(errors.label)}
              helperText={
                errors.label ?? `${draft?.label.trim().length ?? 0}/${MAX_LABEL_LENGTH}`
              }
              inputProps={{ 'aria-label': 'Button label' }}
              size="small"
            />
            {draft?.kind === 'reply' && (
              <TextField
                label="Message the rider sends"
                placeholder="Defaults to the label"
                value={draft.value}
                onChange={set('value')}
                error={Boolean(errors.value)}
                helperText={errors.value}
                inputProps={{ 'aria-label': 'Message the rider sends' }}
                size="small"
                multiline
                maxRows={4}
              />
            )}
            {draft?.kind === 'open_url' && (
              <TextField
                label="Link"
                placeholder="https://"
                value={draft.value}
                onChange={set('value')}
                error={Boolean(errors.value)}
                helperText={errors.value ?? 'Only https links'}
                inputProps={{ 'aria-label': 'Link', inputMode: 'url' }}
                size="small"
              />
            )}
            {rentalField}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={blocked}>
            Add button
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/**
 * The composer's "Buttons" control: pick a kind, fill its form, and the
 * button rides on the next reply. At most 4 per reply. Opening the menu
 * reads the rider's rentals; a rental kind with none to act on is off.
 */
export function ActionsMenuButton({
  count,
  riderId,
  anchoredRentalId,
  disabled,
  onAdd,
}: {
  count: number;
  /** The rider's ant user id, whose rentals the rental kinds pick from. */
  riderId: string | null | undefined;
  anchoredRentalId: string | null | undefined;
  disabled?: boolean;
  onAdd: (action: ChatMessageActionInput) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<ActionDraft | null>(null);
  const rentals = useRiderRentals(riderId, anchoredRentalId);
  const full = count >= MAX_ACTIONS;

  const noRental = (kind: ChatActionKind) =>
    rentals.status === 'ready' &&
    Boolean(actionKindInfo(kind)?.needsRental) &&
    rentalChoices(kind, rentals.active, rentals.endedAnchor).length === 0;

  const pick = (kind: ChatActionKind) => {
    setAnchor(null);
    setDraft(newActionDraft(kind, anchoredRentalId));
  };

  return (
    <>
      <Tooltip
        title={
          full
            ? `At most ${MAX_ACTIONS} buttons per reply`
            : 'Add a button the rider can tap'
        }
      >
        <span>
          <IconButton
            aria-label="Add a button"
            onClick={(e) => {
              setAnchor(e.currentTarget);
              rentals.load();
            }}
            disabled={disabled || full}
          >
            <SmartButtonOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {ACTION_KINDS.map((k) => {
          const off = noRental(k.kind);
          return (
            // Not disabled: an admin who cannot read the rider's system sees
            // no rentals either, and types the id in the form.
            <MenuItem key={k.kind} onClick={() => pick(k.kind)}>
              <ListItemIcon>{KIND_ICON[k.kind]}</ListItemIcon>
              <ListItemText
                primary={k.title}
                secondary={off ? NO_ACTIVE_RENTAL : k.description}
              />
            </MenuItem>
          );
        })}
      </Menu>
      <ActionFormDialog
        draft={draft}
        rentals={rentals}
        onCancel={() => setDraft(null)}
        onAdd={(action) => {
          setDraft(null);
          onAdd(action);
        }}
      />
    </>
  );
}
